import { useState, useMemo } from 'react';
import { useInventory } from './hooks/useInventory';
import { OldGoldTrade } from './types';
import { useForm } from 'react-hook-form';

export default function OldGoldPurchase() {
  const { oldGold, addOldGold } = useInventory();
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [goldRate, setGoldRate] = useState(6200); // Current gold rate per gram

  // Watch form values for real-time calculation
  const grossWeight = watch('grossWeight');
  const netWeight = watch('netWeight');
  const stoneWeight = watch('stoneWeight');
  const purity = watch('purity');

  // Auto-calculate gross weight when net weight or stone weight changes
  useMemo(() => {
    const net = parseFloat(netWeight) || 0;
    const stone = parseFloat(stoneWeight) || 0;
    if (net > 0 || stone > 0) {
      const calculatedGross = net + stone;
      if (calculatedGross !== parseFloat(grossWeight)) {
        setValue('grossWeight', calculatedGross.toFixed(2));
      }
    }
  }, [netWeight, stoneWeight, setValue, grossWeight]);

  // Memoize calculation to prevent infinite re-renders
  const calculatedValues = useMemo(() => {
    const gross = parseFloat(grossWeight) || 0;
    const purityValue = parseFloat(purity) || 0;
    
    if (gross > 0 && purityValue > 0) {
      const purityFactor = purityValue / 100;
      const baseValue = gross * goldRate * purityFactor;
      const calculatedDeduction = baseValue * 0.05; // 5% deduction
      return {
        valuation: baseValue,
        deduction: calculatedDeduction,
        finalValue: baseValue - calculatedDeduction
      };
    }
    return { valuation: 0, deduction: 0, finalValue: 0 };
  }, [grossWeight, purity, goldRate]);

  const onSubmit = async (data: any) => {
    try {
      const gross = parseFloat(data.grossWeight);
      const net = parseFloat(data.netWeight) || 0;
      const stone = parseFloat(data.stoneWeight) || 0;
      
      // Validate weight calculations
      if (gross <= 0) {
        alert('Gross weight must be greater than zero');
        return;
      }
      
      if (Math.abs(gross - (net + stone)) > 0.01) {
        alert('Gross weight must equal net weight + stone weight');
        return;
      }

      const tradeData = {
        ...data,
        id: crypto.randomUUID(),
        date: new Date().toISOString().split('T')[0],
        grossWeight: gross,
        netWeight: net,
        stoneWeight: stone,
        purity: parseFloat(data.purity),
        valuation: calculatedValues.valuation,
        deduction: calculatedValues.deduction,
        finalValue: calculatedValues.finalValue
      };
      await addOldGold.mutateAsync(tradeData);
      reset();
    } catch (error) {
      console.error('Failed to add old gold trade:', error);
    }
  };

  const filteredTrades = oldGold.data?.filter((trade: OldGoldTrade) =>
    trade.customer.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (oldGold.isLoading) return <div className="p-6">Loading old gold trades...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-primary">Old Gold Purchase</h1>
          <p className="text-sm text-gray-600 mt-1">Current Gold Rate: ₹{goldRate}/gram</p>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="number"
            value={goldRate}
            onChange={(e) => setGoldRate(parseFloat(e.target.value) || 0)}
            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
            placeholder="Rate"
          />
          <span className="text-sm text-gray-600">₹/gram</span>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <input
          type="text"
          placeholder="Search by customer name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
              <i className="ri-coins-line text-yellow-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Purchases</p>
              <p className="text-2xl font-semibold text-primary">{filteredTrades.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <i className="ri-money-rupee-circle-line text-green-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-semibold text-primary">
                ₹{filteredTrades.reduce((sum: number, trade: OldGoldTrade) => sum + (trade.finalValue || 0), 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <i className="ri-scales-3-line text-blue-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Weight</p>
              <p className="text-2xl font-semibold text-primary">
                {filteredTrades.reduce((sum: number, trade: OldGoldTrade) => sum + (trade.grossWeight || 0), 0).toFixed(2)}g
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Trade Form */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold text-primary mb-4">New Old Gold Purchase</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <input 
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
              placeholder="Customer Name" 
              {...register('customer', { required: 'Customer name is required' })} 
            />
            <input 
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
              placeholder="Item Description" 
              {...register('itemDesc', { required: 'Item description is required' })} 
            />
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Net Weight (g)</label>
              <input 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
                type="number" 
                step="0.01"
                min="0.01"
                placeholder="Net Weight" 
                {...register('netWeight', { 
                  required: 'Net weight is required',
                  min: { value: 0.01, message: 'Net weight must be greater than 0' }
                })} 
              />
              {errors.netWeight && <p className="text-red-500 text-xs mt-1">{errors.netWeight.message as string}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stone Weight (g)</label>
              <input 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
                type="number" 
                step="0.01"
                min="0"
                placeholder="Stone Weight" 
                {...register('stoneWeight', { 
                  min: { value: 0, message: 'Stone weight cannot be negative' }
                })} 
              />
              {errors.stoneWeight && <p className="text-red-500 text-xs mt-1">{errors.stoneWeight.message as string}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gross Weight (g)</label>
              <input 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-gray-50" 
                type="number" 
                step="0.01"
                placeholder="Auto-calculated" 
                readOnly
                {...register('grossWeight')} 
              />
              <p className="text-xs text-gray-500 mt-1">Auto-calculated: Net + Stone</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <select 
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
              {...register('purity', { required: 'Purity is required' })}
            >
              <option value="">Select Purity</option>
              <option value="91.6">22K (91.6%)</option>
              <option value="75">18K (75%)</option>
              <option value="58.3">14K (58.3%)</option>
              <option value="50">12K (50%)</option>
            </select>
            
            <select 
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
              {...register('paymentMode', { required: 'Payment mode is required' })}
            >
              <option value="">Select Payment Mode</option>
              <option value="cash">Cash</option>
              <option value="transfer">Bank Transfer</option>
              <option value="adjusted">Adjusted Against Purchase</option>
            </select>
          </div>
          
          {/* Calculation Display */}
          {grossWeight && purity && parseFloat(grossWeight) > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Base Valuation:</span>
                  <span className="font-medium ml-2">₹{calculatedValues.valuation.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-600">Deduction (5%):</span>
                  <span className="font-medium ml-2 text-red-600">-₹{calculatedValues.deduction.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-600">Final Value:</span>
                  <span className="font-semibold ml-2 text-green-600">₹{calculatedValues.finalValue.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
          
          <button 
            type="submit"
            disabled={addOldGold.isPending}
            className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {addOldGold.isPending ? 'Processing...' : 'Complete Purchase'}
          </button>
        </form>
      </div>

      {/* Trade Records */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-semibold text-primary">Trade Records</h2>
        </div>
        {filteredTrades.length === 0 ? (
          <div className="p-8 text-center">
            <i className="ri-coins-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 mb-2">No old gold trades yet</p>
            <p className="text-sm text-gray-400">Complete your first purchase to see records here</p>
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-accent/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-primary">Date</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Customer</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Item</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Net Wt</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Stone Wt</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Gross Wt</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Purity</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Valuation</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Final Value</th>
                <th className="px-4 py-3 text-left font-medium text-primary">Payment</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.map((trade: OldGoldTrade) => (
                <tr key={trade.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">{new Date(trade.date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 font-medium">{trade.customer}</td>
                  <td className="px-4 py-3">{trade.itemDesc}</td>
                  <td className="px-4 py-3">{(trade.netWeight || 0).toFixed(2)}g</td>
                  <td className="px-4 py-3">{(trade.stoneWeight || 0).toFixed(2)}g</td>
                  <td className="px-4 py-3 font-medium">{(trade.grossWeight || 0).toFixed(2)}g</td>
                  <td className="px-4 py-3">{trade.purity}%</td>
                  <td className="px-4 py-3">₹{(trade.valuation || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 font-medium text-green-600">₹{(trade.finalValue || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      trade.paymentMode === 'cash' ? 'bg-green-100 text-green-800' :
                      trade.paymentMode === 'transfer' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {trade.paymentMode}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}